// Shared by the type editor and API. No database/browser dependencies.
const CATEGORY = 'timesheet_submission_extension';
const SECTIONS = ['passport', 'form', 'participants', 'notifications'];
const TARGETS = ['passport-basics', 'passport-appearance', 'form-files', 'form-comments',
    'participants-approvers', 'participants-observers', 'notifications-statuses', 'notifications-stale'];
const NOTIFICATION_STATUSES = ['submitted', 'completed', 'rejected', 'cancelled'];
const NOTIFICATION_ROLES = ['initiator', 'approvers', 'observers'];
const isExtension = category => category === CATEGORY;
const copy = value => JSON.parse(JSON.stringify(value || {}));

// Files can be added independently of the protected request contents, until the
// first approval, by the same roles that normally supply the request details.
function canUploadFiles(request, participants, userId) {
    return Boolean(request && ['submitted', 'in_review'].includes(request.status)
        && !(request.RequestType && request.RequestType.allow_attachments === false)
        && !participants.some(p => p.role === 'approver' && p.status === 'approved')
        && participants.some(p => Number(p.user_id) === Number(userId)
            && ['initiator', 'approver'].includes(p.role)));
}

function normalizeWorkflow(value, allowComments = true) {
    const result = copy(value);
    Object.assign(result, {
        domain: CATEGORY,
        allowedActions: ['submit', 'approve', 'reject', 'cancel'],
        requiredRoles: ['initiator', 'approver'], optionalRoles: ['observer'],
        sequentialApproval: false, requiresAcceptance: false, autoTransitions: {},
        requestEditing: {editorRoles: []}, approverManagement: {},
        requiresBuildingProcess: false,
        stateMachineConfig: {
            transitionsMap: {draft: ['submitted'],
                submitted: ['in_review', 'completed', 'rejected', 'cancelled'],
                in_review: ['in_review', 'completed', 'rejected', 'cancelled'],
                completed: [], rejected: [], cancelled: []},
            actionPermissions: {submit: ['initiator'], approve: ['approver'], reject: ['approver'], cancel: ['initiator'],
                comment: allowComments ? ['initiator', 'approver', 'observer'] : []},
            actionToStatus: {submit: 'submitted', approve: 'in_review', reject: 'rejected', cancel: 'cancelled'},
            customLogic: {approve: {fromSubmitted: 'in_review', hasPendingApprovers: 'in_review', allApproved: 'completed'}}
        }
    });
    // Keep saved rule contents, but these capabilities cannot run for this domain.
    ['buildingProcess', 'itemCatalog', 'requiredAttachments', 'linkedRequests', 'fuelCardAction',
        'mechanicAssetAction', 'employeeCatalogSubject', 'excelExport'].forEach(key => {
        result[key] = Object.assign({}, result[key], {enabled: false});
    });
    result.itemCatalog.ppe = Object.assign({}, result.itemCatalog.ppe, {enabled: false,
        material_group_ids: Array.isArray(result.itemCatalog.ppe && result.itemCatalog.ppe.material_group_ids)
            ? result.itemCatalog.ppe.material_group_ids : []});
    result.buildingProcess.required = false;
    result.excelExport.generateOnNotification = false;
    result.descriptionField = Object.assign({}, result.descriptionField, {enabled: true, required: true});
    result.closingComment = Object.assign({}, result.closingComment, {required: false});
    result.deadlinesConfig = Object.assign({}, result.deadlinesConfig, {autoCloseAwaitingAcceptanceEnabled: false});
    if (Array.isArray(result.mechanicAssetActions)) {
        result.mechanicAssetActions = result.mechanicAssetActions.map(action => Object.assign({}, action, {enabled: false}));
    }
    return result;
}

function notificationDefaults(value) {
    const result = copy(value);
    NOTIFICATION_STATUSES.forEach(status => {
        if (!result[status]) result[status] = {enabled: true, send_notifications: true,
            notify_to: [status === 'submitted' ? 'approvers' : 'initiator', 'observers']};
        const aliases = {approver: 'approvers', observer: 'observers'};
        if (Array.isArray(result[status].notify_to)) result[status].notify_to = [...new Set(result[status].notify_to
            .map(role => aliases[role] || role).filter(role => NOTIFICATION_ROLES.includes(role)))];
    });
    // Partial approvals are deliberately silent, including for observers.
    result.in_review = Object.assign({}, result.in_review, {send_notifications: false, notify_to: []});
    return result;
}

module.exports = {CATEGORY, SECTIONS, TARGETS, NOTIFICATION_STATUSES, NOTIFICATION_ROLES,
    isExtension, normalizeWorkflow, notificationDefaults, canUploadFiles};
